import { SearchModel } from './common';

/**
 * Tipos para Pedidos de Venda conforme schema [Financial].[Order]
 *
 * OrderStatus IDs (tabela [Financial].[OrderStatus]):
 *   1 — Em Aberto        — pedido aguardando pagamento
 *   2 — Venda Agenciada  — pedido pago aguardando próximo status
 *   3 — Em Digitação     — pedido aguardando estoque
 *   4 — Em Andamento     — pedido pago aguardando separação/expedição
 *   5 — Em Conferência   — pedido em conferência pela logística
 *   6 — Conferido        — pedido conferido pela logística
 *   8 — Atendido         — pedido entregue/concluído
 *   9 — Cancelado        — pedido cancelado
 *   10 — Enviado          — pedido enviado para cliente, aguardando entrega
 *   11 — Entregue         — pedido entregue ao cliente
 *   12 — Desmembrado Pai   — pedido pai que foi desmembrado em dois pedidos filhos
 */

// ─── Status IDs (enum numérico) ─────────────────────────────────

/**
 * IDs dos status de pedido conforme tabela [Financial].[OrderStatus].
 *
 * O id 7 ("Em Demonstração") foi APOSENTADO em 09/2026: virou o TIPO de pedido "Demonstração"
 * (OrderTypeId = 2). Status é a etapa no fluxo, tipo é a natureza do pedido — como tipo, a
 * demonstração percorre o fluxo logístico inteiro sem perder a identificação. O id não é
 * reaproveitado (o IDENTITY da tabela segue em 12).
 */
export enum OrderStatusId {
  EmAberto = 1,
  VendaAgenciada = 2,
  EmDigitacao = 3,
  EmAndamento = 4,
  EmConferencia = 5,
  Conferido = 6,
  Atendido = 8,
  Cancelado = 9,
  Enviado = 10,
  Entregue = 11,
  DesmembradoPai = 12,
}

/** IDs dos tipos de pedido conforme tabela [Financial].[OrderType] */
export enum OrderTypeId {
  Venda = 1,
}

/**
 * Status que um pedido de venda não pago não pode assumir: "Em Andamento" (4) e "Atendido" (8).
 * Ambos significam que o pedido está sendo atendido, o que não pode ocorrer antes do pagamento.
 * Espelha domain.IsUnpaidSaleBlockedStatus no ERPApi — o backend rejeita a troca com HTTP 409.
 */
export const UNPAID_SALE_BLOCKED_STATUS_IDS: readonly number[] = [
  OrderStatusId.EmAndamento,
  OrderStatusId.Atendido,
];

/**
 * Pedido de venda ([OrderTypeId] = 1) ainda não pago ([IsPayed] = 0).
 * `is_payed` ausente/null conta como não pago: a flag só é marcada quando o pagamento é
 * confirmado. Espelha domain.IsUnpaidSaleOrder no ERPApi.
 */
export const isUnpaidSaleOrder = (
  order: Pick<SalesOrder, 'order_type_id' | 'is_payed'>,
): boolean => order.order_type_id === OrderTypeId.Venda && order.is_payed !== true;

/**
 * Pedido pai desmembrado cujo desmembramento perdeu o efeito: ele tem filhos e todos foram
 * cancelados. Nesse caso o pai — que mantém todos os seus itens, já que o desmembramento só cria
 * os filhos e troca o status do pai — volta a aceitar alteração de status.
 * Espelha domain.AllChildOrdersCancelled no ERPApi, que libera a mesma troca em UpdateOrderStatus.
 */
export const isRevivableDesmembradoParent = (
  order: Pick<SalesOrder, 'order_status_id' | 'child_orders'>,
): boolean => {
  if (order.order_status_id !== OrderStatusId.DesmembradoPai) return false;
  const children = order.child_orders ?? [];
  if (children.length === 0) return false;
  return children.every(child =>
    // Pedidos antigos podem vir sem order_status_id; aí cai para o nome do status.
    child.order_status_id != null
      ? child.order_status_id === OrderStatusId.Cancelado
      : child.order_status === 'Cancelado',
  );
};

/**
 * A NF emitida trava o campo de status no front — exceto quando o pedido ainda
 * está no começo do fluxo: "Em Aberto" (1) ou "Venda Agenciada" (2). Nesses
 * status a logística precisa mover o pedido para "Em Andamento" mesmo com NF já
 * emitida (a NF costuma sair antes da separação). O backend nunca teve esse
 * bloqueio para 1/2 — BlockOrderChange é 0 nos dois; a trava era só do front.
 */
export const isStatusEditableWithInvoice = (orderStatusId?: number | null): boolean =>
  orderStatusId === OrderStatusId.EmAberto || orderStatusId === OrderStatusId.VendaAgenciada;

/**
 * Pedido cuja nota fiscal já tem NF de devolução vinculada. É o caso da devolução fora do prazo:
 * a mercadoria volta no mês seguinte à emissão, a SEFAZ já não aceita o cancelamento da nota, e a
 * saída fiscal é emitir uma NF de devolução referenciando a de venda. A partir daí o pedido pode
 * ser cancelado sem desvincular a NF — que continua válida, agora com a devolução atrelada.
 *
 * Basta UMA devolução não cancelada (inclusive parcial). Espelha domain.CanCancelDespiteBlockedStatus
 * no ERPApi, que abre a mesma exceção no BlockOrderChange do UpdateOrderStatus.
 */
export const canCancelDueToReturnedInvoice = (
  order: Pick<SalesOrder, 'invoice_has_return' | 'order_status_id'>,
): boolean =>
  order.invoice_has_return === true && order.order_status_id !== OrderStatusId.Cancelado;

/**
 * Permissão `orders.change_status_blocked` (operador logístico): destrava o campo de status de pedido
 * em Atendido/Enviado/Entregue — status com BlockOrderChange — enquanto ele não tem NF. É o mesmo
 * alcance que o finance tem pela tela (com NF o status fica travado para todos). Cancelado e
 * Desmembrado Pai ficam de fora. Espelha domain.CanOverrideBlockedStatus no ERPApi.
 */
export const canChangeStatusOfBlockedOrder = (
  order: Pick<SalesOrder, 'order_status_id' | 'invoice_id'>,
  canChangeBlockedStatus: boolean,
): boolean =>
  canChangeBlockedStatus &&
  !order.invoice_id &&
  (order.order_status_id === OrderStatusId.Atendido ||
    order.order_status_id === OrderStatusId.Enviado ||
    order.order_status_id === OrderStatusId.Entregue);

/**
 * Permissão `orders.change_carrier_blocked` (operador logístico): destrava a cotação de frete — a
 * troca de transportadora — de pedido Atendido sem NF. Só a cotação: itens, volumes e o resto do
 * pedido seguem travados. Espelha domain.CanChangeCarrierDespiteBlock no ERPApi.
 */
export const canChangeCarrierOfBlockedOrder = (
  order: Pick<SalesOrder, 'order_status_id' | 'invoice_id'>,
  canChangeCarrierBlocked: boolean,
): boolean =>
  canChangeCarrierBlocked && !order.invoice_id && order.order_status_id === OrderStatusId.Atendido;

/**
 * Um pedido "Desmembrado Pai" (status 12) é escrituração — os filhos carregam o envio real — e por
 * padrão tem os itens bloqueados para edição. Admin e finance podem removê-los e recalcular o pai;
 * o backend (decideOrderEditable no ERPApi) já aceita a operação para esses perfis.
 *
 * Esta função decide, do lado do front, se os itens do PAI devem ficar liberados: só para o próprio
 * pai (sem parent_order_id — filhos seguem bloqueados), enquanto não houver NF emitida, e apenas
 * para quem tem o override de BlockOrderChange (admin/finance, via `canOverrideBlockOrderChange`).
 * Espelha a liberação por papel do backend, inclusive quando o pai está pago.
 */
export const canEditDesmembradoParentItems = (
  order: Pick<SalesOrder, 'order_status_id' | 'parent_order_id' | 'invoice_id'>,
  canOverrideBlockOrderChange: boolean,
): boolean =>
  order.order_status_id === OrderStatusId.DesmembradoPai &&
  !order.parent_order_id &&
  !order.invoice_id &&
  canOverrideBlockOrderChange;

/** Mensagens dos bloqueios de pedido de venda não pago (alinhadas com as do ERPApi). */
export const UNPAID_SALE_STATUS_BLOCK_MSG =
  'Pedido de venda não pago não pode ser movido para "Em Andamento" ou "Atendido". Registre o pagamento antes de avançar o status.';
export const UNPAID_SALE_INVOICE_BLOCK_MSG =
  'Pedido de venda não pago não pode ter nota fiscal emitida. Registre o pagamento antes de emitir a NF.';

// ─── Status Options ──────────────────────────────────────────────

/** Tipo retornado pela API GET /api/v1/order-statuses */
export interface OrderStatusOption {
  order_status_id: number;
  status_name: string;
  can_user_choose_manually: boolean;
  block_order_change: boolean;
  allocate_available_stock: boolean;
}

/**
 * Mapa de ID → cor para badges de status (usado para renderização visual).
 * Mantido como constante pois as cores são responsabilidade do frontend.
 */
export const STATUS_COLOR_MAP: Record<number, string> = {
  [OrderStatusId.EmAberto]: 'yellow',
  [OrderStatusId.VendaAgenciada]: 'purple',
  [OrderStatusId.EmDigitacao]: 'slate',
  [OrderStatusId.EmAndamento]: 'blue',
  [OrderStatusId.EmConferencia]: 'orange',
  [OrderStatusId.Conferido]: 'cyan',
  [OrderStatusId.Atendido]: 'green',
  [OrderStatusId.Cancelado]: 'red',
  [OrderStatusId.Enviado]: 'indigo',
  [OrderStatusId.Entregue]: 'teal',
  [OrderStatusId.DesmembradoPai]: 'gray',
};

/**
 * Lista estática de status usada como fallback para badges, filtros e selects.
 * A fonte de verdade é a API GET /api/v1/order-statuses.
 * `id` corresponde a OrderStatusId.
 */
export const SALES_ORDER_STATUS_OPTIONS = [
  { id: OrderStatusId.EmAberto, value: 'Em Aberto', label: 'Em Aberto', color: 'yellow' },
  { id: OrderStatusId.VendaAgenciada, value: 'Venda Agenciada', label: 'Venda Agenciada', color: 'purple' },
  { id: OrderStatusId.EmDigitacao, value: 'Em Digitação', label: 'Em Digitação', color: 'slate' },
  { id: OrderStatusId.EmAndamento, value: 'Em Andamento', label: 'Em Andamento', color: 'blue' },
  { id: OrderStatusId.EmConferencia, value: 'Em Conferência', label: 'Em Conferência', color: 'orange' },
  { id: OrderStatusId.Conferido, value: 'Conferido', label: 'Conferido', color: 'cyan' },
  { id: OrderStatusId.Atendido, value: 'Atendido', label: 'Atendido', color: 'green' },
  { id: OrderStatusId.Cancelado, value: 'Cancelado', label: 'Cancelado', color: 'red' },
  { id: OrderStatusId.Enviado, value: 'Enviado', label: 'Enviado', color: 'indigo' },
  { id: OrderStatusId.Entregue, value: 'Entregue', label: 'Entregue', color: 'teal' },
  { id: OrderStatusId.DesmembradoPai, value: 'Desmembrado Pai', label: 'Desmembrado Pai', color: 'gray' },
] as const;

/** Busca uma opção de status pelo ID numérico. */
export const getStatusOptionById = (id?: number) =>
  SALES_ORDER_STATUS_OPTIONS.find(o => o.id === id);

/** Busca uma opção de status pelo nome (case-insensitive). */
export const getStatusOptionByName = (name?: string) =>
  name ? SALES_ORDER_STATUS_OPTIONS.find(o => o.value.toLowerCase() === name.toLowerCase()) : undefined;

/** @deprecated Usar os dados retornados por salesOrderService.getOrderStatuses() */
export const SALES_ORDER_MANUAL_STATUS_OPTIONS = SALES_ORDER_STATUS_OPTIONS;

/**
 * Converte a lista da API em options para selects.
 * Filtra apenas os status que o usuário pode selecionar manualmente
 * e aplica cores/labels canônicos do frontend.
 */
export const toManualStatusOptions = (apiStatuses: OrderStatusOption[]) => {
  const manualIds = new Set(
    apiStatuses
      .filter(s => s.can_user_choose_manually)
      .map(s => s.order_status_id),
  );
  return SALES_ORDER_STATUS_OPTIONS
    .filter(s => manualIds.has(s.id))
    .map(s => ({ id: s.id, value: s.value, label: s.label, color: s.color as string }));
};

// ─── Customer ────────────────────────────────────────────────────

export interface CustomerDefaultAddress {
  addr_id?: number;
  addr_type?: number;
  addr_label?: string;
  addr_street?: string;
  addr_num?: string;
  addr_compl?: string;
  addr_district?: string;
  addr_city?: string;
  addr_state?: string;
  addr_zip?: string;
  addr_country?: string;
}

export interface Customer {
  customer_id: number;
  customer_erp_id?: number;
  name?: string;
  document?: string;
  person_type?: number;
  addr_street?: string;
  addr_num?: string;
  addr_compl?: string;
  addr_district?: string;
  addr_city?: string;
  addr_state?: string;
  addr_zip?: string;
  addr_country?: string;
  cell_phone?: string;
  phone?: string;
  email?: string;
  instagram_profile?: string;
  default_address?: CustomerDefaultAddress;
  /** Inscrição estadual (NF-e dest/IE) */
  state_registration_tax_id?: string | null;
  /** "Isento de Inscrição Estadual" no cadastro (NF-e dest/indIEDest = 2) */
  non_contributor_ie?: boolean | null;
}

// ─── Order Item ──────────────────────────────────────────────────

export interface SalesOrderItem {
  order_item_id?: number;
  order_id?: number;
  sku: string;
  description?: string;
  quantity: number;
  unit_price?: number;
  unit_cost?: number;
  discount?: number;
  is_checkout_required?: boolean;
  is_gift?: boolean | null;
  returned_quantity?: number | null;
  effective_quantity?: number;
  effective_total?: number;
  is_fully_returned?: boolean;
  /** Personalização digitada pelo cliente no site (ex.: "Iniciais"). Read-only: só o intake do ecommerce grava. */
  customization_text?: string | null;
  /** Produto personalizado: id do produto + flags para a thumb da Foto do Projeto (GET /products/:id/project-photo/file). */
  product_id?: number | null;
  is_custom_product?: boolean;
  has_project_photo?: boolean;
  /** Situação do SKU no cadastro: 'ok' | 'inactive' (descrição vem de produto inativo — provavelmente outro produto) | 'missing' (SKU sem produto). Item do site sem tamanho/cor cai aqui; corrigir o SKU antes de faturar. */
  product_status?: 'ok' | 'inactive' | 'missing';
}

// ─── Replacement Order Item (Retirada para Troca) ────────────────

export interface ReplacementOrderItem {
  replacement_order_item_id?: number;
  order_id: number;
  original_order_id: number;
  sku: string;
  description?: string;
  quantity: number;
  /** Valor original do item pago pelo cliente na troca */
  original_value?: number;
  /** Quantidade máxima disponível no pedido original (preenchido pelo front) */
  max_quantity?: number;
}

export interface ReplacementOrderItemPayload {
  original_order_id: number;
  sku: string;
  quantity: number;
  original_value?: number;
}

// ─── Order Volume ────────────────────────────────────────────────

export interface SalesOrderVolume {
  order_volume_id?: number;
  order_id?: number;
  height: number;
  width: number;
  length: number;
  diameter: number;
  weight: number;
  company_id?: number;
  order_num_erp?: number;
  /** Numeração explícita do volume ("i" em "Volume i de N") */
  volume_number?: number;
  /** Itens do pedido atribuídos a este volume (atribuição sempre opcional) */
  items?: VolumeItem[];
}

/** Item do pedido atribuído a um volume (retornado pela API) */
export interface VolumeItem {
  order_volume_item_id?: number;
  order_volume_id?: number;
  order_item_id: number;
  sku?: string;
  product_name?: string;
  quantity: number;
  declared_value?: number;
}

/** Payload para substituir os itens atribuídos a um volume */
export interface VolumeItemsPayload {
  items: {
    order_item_id: number;
    quantity: number;
    declared_value?: number;
  }[];
}

// ─── Order Installment ──────────────────────────────────────────

export interface SalesOrderInstallment {
  order_installment_id?: number;
  order_id?: number;
  value: number;
  due_date: string;
  notes?: string;
  payment_method_id?: number;
  payment_method_name?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  last_updated_by?: string;
}

// ─── Order (cabeçalho) ──────────────────────────────────────────

export interface SalesOrder {
  order_id: number;
  company_id: number;
  company_name?: string;
  company_document?: string;
  order_num_erp: number;
  order_num_ecommerce?: string;
  order_date: string;
  observation?: string;
  internal_observation?: string;
  seller?: string;
  shipping_price?: number;
  freight_discount_value?: number;
  delivery_time_chosen_by_customer?: number;
  shipping_chosen_by_customer?: string;
  /** Transportadora/serviço REAIS escolhidos no checkout (site/CRM), como o FreightHub cotou. */
  chosen_provider_name?: string | null;
  chosen_provider_service_name?: string | null;
  chosen_provider_id?: number | null;
  chosen_provider_service_id?: number | null;
  order_products_total?: number;
  order_sell_total?: number;
  order_status?: string;
  order_status_id?: number;
  stock_updated?: boolean | null;
  stock_updated_timestamp?: string | null;
  customer_id?: number;
  customer_name?: string;
  customer_document?: string;
  customer_email?: string;
  customer_cell_phone?: string;
  /** Inscrição estadual do cliente (NF-e dest/IE) */
  customer_state_registration_tax_id?: string | null;
  /** "Isento de Inscrição Estadual" no cadastro do cliente (NF-e dest/indIEDest = 2) */
  customer_non_contributor_ie?: boolean | null;
  invoice_id?: number;
  /** Invoice number (populated by backend JOIN with Invoice table) */
  invoice_number?: number;
  /**
   * A NF do pedido (de venda ou de bonificação) já tem NF de devolução vinculada
   * ([Financial].[InvoiceReference]). Calculado pelo backend; libera o cancelamento do pedido.
   */
  invoice_has_return?: boolean | null;
  processing_status: number;
  ship_addr_street?: string;
  ship_addr_num?: string;
  ship_addr_compl?: string;
  ship_addr_district?: string;
  ship_addr_city?: string;
  ship_addr_state?: string;
  ship_addr_zip?: string;
  po_commission_percent?: number;
  po_commission_value?: number;
  tax_percent?: number;
  tax_value?: number;
  payment_method_fee_percent?: number;
  payment_method_fee?: number;
  seller_commission_percent?: number;
  seller_commission_value?: number;
  product_cost_sum?: number;
  total_order_cost?: number;
  order_earn?: number;
  order_earn_percent?: number;
  order_discount?: number;
  other_expenses?: number;
  payment_method_description?: string;
  payment_method_erp_id?: number;
  proposal_num?: number;
  shipping_status?: string;
  shipping_track_url?: string;
  shipping_update_timestamp?: string;
  shipping_timestamp?: string | null;
  delivery_timestamp?: string | null;
  /** Transportadora que de fato enviou o pedido (frota própria / integração), resolvida pela API.
   *  Diferente de shipping_chosen_by_customer, que é só a opção oferecida no checkout.
   *  Só vem no detalhe do pedido (GET /orders/:id), não na listagem. */
  shipping_modality?: string;
  /** Data limite prometida ao cliente (YYYY-MM-DD): delivery_time_chosen_by_customer dias úteis
   *  a partir de order_date, pulando fins de semana e feriados. */
  delivery_deadline?: string;
  /** Prazo estourado e pedido ainda não entregue. Sempre false para pedidos cancelados,
   *  entregues e desmembrados pai. */
  is_delivery_late?: boolean;
  is_payed?: boolean | null;
  payment_date?: string | null;
  followup_done?: boolean;
  followup_done_timestamp?: string | null;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  last_updated_by?: string;
  parent_order_id?: number;
  // Sub-resources
  items?: SalesOrderItem[];
  volumes?: SalesOrderVolume[];
  installments?: SalesOrderInstallment[];
  freight_quotes?: OrderFreightQuote[];
  replacement_items?: ReplacementOrderItem[];
  // Prioridade e hora requisitada de entrega
  is_priority?: boolean | null;
  request_delivery_time?: string | null;
  /** Envio agendado (YYYY-MM-DD): checkout ok, mas não entra em rota nem gera envio antes da data. */
  scheduled_ship_date?: string | null;
  // Tipo de pedido
  order_type_id?: number | null;
  order_type_name?: string;
  // Canal de venda
  sales_channel_id?: number | null;
  sales_channel_name?: string;
  // Nota fiscal de presente
  gift_invoice_id?: number | null;
  /** Gift invoice number (populated by backend JOIN with Invoice table) */
  gift_invoice_number?: number | null;
  /**
   * Indica se o pedido possui ao menos um item com `is_gift = true`.
   * Calculado pelo backend para que a listagem possa decidir exibir o
   * bot\u00e3o de "Emitir NF Bonifica\u00e7\u00e3o" sem precisar carregar os itens.
   */
  has_gift_items?: boolean | null;
  /** Valor de desconto de itens de devolução (troca) */
  product_replacement_discount_value?: number;
  /** Indica se é a primeira compra do cliente (preenchido automaticamente na criação) */
  is_first_purchase?: boolean | null;
  // Referências de desmembramento
  child_orders?: { order_id: number; order_num_erp: number; order_status?: string; order_status_id?: number }[];
}

// ─── Search Model ────────────────────────────────────────────────

export interface SalesOrderSearchModel extends SearchModel {
  company_id?: number;
  order_status?: string;
  order_status_id?: number;
  /** Tipo de pedido (demonstração, venda, brinde…). */
  order_type_id?: number;
  date_from?: string;
  date_to?: string;
  customer_name?: string;
}

// ─── Split (Desmembramento) ──────────────────────────────────────

export interface SplitOrderItemAllocation {
  order_item_id?: number;
  sku: string;
  description?: string;
  total_quantity: number;
  order1_quantity: number;
  order2_quantity: number;
  unit_price?: number;
  unit_cost?: number;
  discount?: number;
}

export interface SplitOrderPayload {
  parent_order_id: number;
  order1_status_id: number;
  order2_status_id: number;
  order1_items: { sku: string; quantity: number; unit_price?: number; unit_cost?: number; discount?: number }[];
  order2_items: { sku: string; quantity: number; unit_price?: number; unit_cost?: number; discount?: number }[];
  /** Pedido filho que receberá os itens de troca (replacement_items). Se null, itens de troca ficam no pedido 1. */
  replacement_items_target_order?: 1 | 2;
  /**
   * Quando true, o pedido sendo desmembrado já é filho de outro pedido.
   * Nesse caso o backend deve:
   *   - MANTER o pedido atual (source_order_id, com order1_items)
   *   - CRIAR apenas UM novo pedido irmão (order2_items) filho do mesmo pai original (parent_order_id)
   *   - NÃO criar dois pedidos novos
   */
  is_resplit?: boolean;
  /** ID do pedido sendo desmembrado. Obrigatório quando is_resplit = true (pedido filho que será mantido com order1_items). */
  source_order_id?: number;
}

// ─── Clone ───────────────────────────────────────────────────────

export interface CloneOrderPayload {
  source_order_id: number;
}

// ─── Payment Method ──────────────────────────────────────────────

export interface OrderPaymentMethod {
  payment_method_id: number;
  payment_method_name: string;
  is_product_credit_4_return?: boolean;
}

// ─── Seller ──────────────────────────────────────────────────────

export interface Seller {
  seller_id: number;
  name: string;
  active?: boolean;
}

// ─── Order Freight Quote ─────────────────────────────────────────

export interface OrderFreightQuote {
  order_freight_quote_id: number;
  order_volume_id?: number;
  order_id: number;
  cost: number;
  delivery_days: number;
  provider_id: number;
  provider_service_id: number;
  provider_name?: string;
  provider_service_name?: string;
  is_selected: boolean;
  /** Opção "Entrega própria (sem cotação)" da frota própria (derivado no ERPApi). */
  is_manual?: boolean;
  /** Transportadora/serviço bloqueado para a empresa (roteamento de pagamentos); não pode ser selecionada. */
  blocked?: boolean;
  blocked_reason?: string | null;
}

export interface FreightQuoteSelectPayload {
  order_freight_quote_id: number;
  is_selected: boolean;
}

// ─── CRUD Payloads ───────────────────────────────────────────────

export type VolumePayload = Omit<SalesOrderVolume, 'order_volume_id' | 'order_id' | 'company_id' | 'order_num_erp' | 'volume_number' | 'items'>;
export type ItemPayload = Omit<SalesOrderItem, 'order_item_id' | 'order_id'>;
export type InstallmentPayload = Omit<SalesOrderInstallment, 'order_installment_id' | 'order_id' | 'created_at' | 'updated_at' | 'created_by' | 'last_updated_by'>;

export interface ShippingAddressPayload {
  ship_addr_street?: string;
  ship_addr_num?: string;
  ship_addr_compl?: string;
  ship_addr_district?: string;
  ship_addr_city?: string;
  ship_addr_state?: string;
  ship_addr_zip?: string;
}

// ─── Create Order Payload ─────────────────────────────────────────

export interface SalesOrderCreatePayload {
  company_id: number;
  order_date: string;
  order_status_id?: number;
  order_type_id?: number;
  customer_id?: number;
  customer_name?: string;
  seller?: string;
  observation?: string;
  internal_observation?: string;
  shipping_price?: number;
  freight_discount_value?: number;
  shipping_chosen_by_customer?: string;
  delivery_time_chosen_by_customer?: number;
  payment_method_description?: string;
  is_payed?: boolean;
  payment_date?: string;
  ship_addr_street?: string;
  ship_addr_num?: string;
  ship_addr_compl?: string;
  ship_addr_district?: string;
  ship_addr_city?: string;
  ship_addr_state?: string;
  ship_addr_zip?: string;
  order_discount?: number;
  items?: ItemPayload[];
  installments?: InstallmentPayload[];
  is_priority?: boolean;
  request_delivery_time?: string;
  scheduled_ship_date?: string;
}

// ─── Histórico/auditoria de alterações do pedido ────────────────

export interface OrderFieldChange {
  field: string;
  label: string;
  old: string;
  new: string;
  kind: 'added' | 'removed' | 'modified';
}

export interface OrderChangeEntry {
  order_change_request_id: number;
  order_id: number;
  change_type?: string;
  changed_by?: string;
  order_status_id?: number;
  order_status?: string;
  old_order_status_id?: number;
  old_order_status?: string;
  changes: OrderFieldChange[];
  raw_json?: string;
  change_timestamp?: string;
}
